import React from "react"
import PropTypes from "prop-types"
import moment from "moment"
import { Badge } from "reactstrap"

const activityTypes = {
  eventRegistration: {
    color: "primary",
    icon: "bx-calendar-check",
    label: "Registered for an event",
  },
  eventUnregistration: {
    color: "warning",
    icon: "bx-calendar-x",
    label: "Event registration cancelled",
  },
  updateGameLevel: {
    color: "info",
    icon: "bx-trending-up",
    label: "Game level updated",
  },
}

const hasValue = value => value !== undefined && value !== null && value !== ""

export const humanizeActivityType = type => {
  if (!type) return "Activity recorded"

  const words = String(type)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase()

  return words.replace(/^./, character => character.toUpperCase())
}

export const getActivityMeta = type =>
  activityTypes[type] || {
    color: "secondary",
    icon: "bx-history",
    label: humanizeActivityType(type),
  }

const formatListName = listName => {
  const names = {
    mainList: "Main list",
    preRegistrationList: "Pre-registration list",
    waitingList: "Waiting list",
  }

  return names[listName] || humanizeActivityType(listName)
}

const formatPartnerName = partner => {
  if (!partner || typeof partner !== "object") return null

  const name = [partner.firstName, partner.lastName].filter(Boolean).join(" ")
  return name || partner.email || null
}

const addDetail = (details, label, value) => {
  if (!hasValue(value)) return
  details.push({ label, value: String(value) })
}

export const getActivityDetails = activity => {
  const body = activity?.body || {}
  const extra = body.extra || {}
  const details = []

  addDetail(details, "Event", extra.eventName || body.eventName)
  addDetail(
    details,
    "List",
    body.list ? formatListName(body.list) : undefined
  )
  addDetail(details, "Sport", extra.sportType || body.sportType)
  addDetail(details, "Level", extra.level || body.level)
  addDetail(details, "Draw", extra.draw || body.draw)
  addDetail(details, "Points", extra.points ?? body.points)

  const gameLevel = body.gameLevel || body.player?.gameLevel || extra.gameLevel
  if (gameLevel && typeof gameLevel === "object") {
    addDetail(details, "Singles level", gameLevel.singles)
    addDetail(details, "Doubles level", gameLevel.doubles)
  } else {
    addDetail(details, "Game level", gameLevel)
  }

  addDetail(details, "Partner", formatPartnerName(body.partner))

  return details
}

const getActivityActor = activity => {
  const admin = Array.isArray(activity?.admin)
    ? activity.admin[0]
    : activity?.admin

  if (!admin) {
    return {
      label: "Player",
      supportingText: "Self-service",
    }
  }

  const name = [admin.firstName, admin.lastName].filter(Boolean).join(" ")

  return {
    label: name || admin.email || "Administrator",
    supportingText: "Administrator",
  }
}

const DateCell = ({ activity, value }) => {
  const date = activity?.createdAt || value?.createdAt || value
  const parsedDate = moment(date)

  if (!parsedDate.isValid()) return <span className="text-muted">—</span>

  return (
    <div style={{ minWidth: "128px" }}>
      <div className="font-weight-semibold">
        {parsedDate.format("D MMM YYYY")}
      </div>
      <div className="text-muted font-size-12">
        {parsedDate.format("HH:mm")}
      </div>
    </div>
  )
}

DateCell.propTypes = {
  activity: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
}

const ActivityCell = ({ activity }) => {
  const meta = getActivityMeta(activity?.type)

  return (
    <div style={{ minWidth: "190px" }}>
      <Badge color={meta.color} pill className="px-2 py-1 font-size-12">
        <i className={`bx ${meta.icon} mr-1`} aria-hidden="true" />
        {meta.label}
      </Badge>
      <div className="text-muted font-size-11 mt-1">
        {[activity?.type, activity?.method].filter(Boolean).join(" · ")}
      </div>
    </div>
  )
}

ActivityCell.propTypes = {
  activity: PropTypes.object,
}

const DetailsCell = ({ activity }) => {
  const details = getActivityDetails(activity)

  if (!details.length) {
    return (
      <span className="text-muted font-size-12">
        No additional context was recorded.
      </span>
    )
  }

  return (
    <div className="d-flex flex-wrap" style={{ minWidth: "260px" }}>
      {details.map(detail => (
        <span
          className="badge badge-soft-secondary font-size-11 mr-1 mb-1"
          key={`${detail.label}-${detail.value}`}
        >
          <span className="font-weight-normal">{detail.label}:</span>{" "}
          {detail.value}
        </span>
      ))}
    </div>
  )
}

DetailsCell.propTypes = {
  activity: PropTypes.object,
}

const ActorCell = ({ activity }) => {
  const actor = getActivityActor(activity)

  return (
    <div style={{ minWidth: "120px" }}>
      <div className="font-weight-semibold">{actor.label}</div>
      <div className="text-muted font-size-12">{actor.supportingText}</div>
    </div>
  )
}

ActorCell.propTypes = {
  activity: PropTypes.object,
}

const RecordCell = ({ activity }) => {
  const recordId = activity?._id ? String(activity._id) : null

  if (!recordId) return <span className="text-muted">—</span>

  return (
    <code className="font-size-11 text-muted" title={recordId}>
      …{recordId.slice(-8)}
    </code>
  )
}

RecordCell.propTypes = {
  activity: PropTypes.object,
}

const activityColumns = () => [
  {
    dataField: "createdAt",
    text: "When",
    formatter: (cellContent, activity) => (
      <DateCell activity={activity} value={cellContent} />
    ),
    headerStyle: { width: "14%" },
  },
  {
    dataField: "type",
    text: "Action",
    formatter: (cellContent, activity) => (
      <ActivityCell activity={activity} />
    ),
    headerStyle: { width: "23%" },
  },
  {
    dataField: "body",
    text: "Context",
    formatter: (cellContent, activity) => (
      <DetailsCell activity={activity} />
    ),
    headerStyle: { width: "34%" },
  },
  {
    dataField: "admin",
    text: "Performed by",
    formatter: (cellContent, activity) => <ActorCell activity={activity} />,
    headerStyle: { width: "18%" },
  },
  {
    dataField: "_id",
    text: "Record",
    formatter: (cellContent, activity) => <RecordCell activity={activity} />,
    headerStyle: { width: "11%" },
  },
]

export default activityColumns
